/**
 * @deprecated This component has moved to cinderblocks for shared use. Please use the cinderblocks version instead
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Duration, ZonedDateTime } from '@js-joda/core';
import { Box, Card, Flex, H3, Image, Text } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { bricks } from '@opendoor/bricks/system';

import { LegacyExternalLink } from 'platform/remix-run/react';

import { formatApproxDuration, systemZonedDateTimeFromString } from 'shared/helpers/datetime';
import { HOMEPAGE_URL } from 'shared/helpers/globals';

import { FavoriteButton } from 'buying/components/propertyCards/FavoriteButton';
import { ListingBadges } from 'buying/components/propertyCards/ListingBadges';
import { ListingStatus } from 'buying/components/propertyCards/listingStatus';
import { PropertyCardImageContainer } from 'buying/components/propertyCards/PropertyCardImageContainer';
import { PropertyImageCarousel } from 'buying/components/propertyCards/PropertyImageCarousel';
import { useFavoriteProperty } from 'buying/graphql/useFavoriteProperty';
import { PropertyCardProperty as Property } from 'buying/graphql/useProperties';
import { useRemoveFavoriteProperty } from 'buying/graphql/useRemoveFavoriteProperty';
import { googleStreetViewUrl } from 'buying/helpers/images';
import { useMlsPropertyCardEmitter } from 'buying/MlsComplianceContext';

export const MOBILE_PROPERTY_CARD_WIDTH = 280;
export const MIN_PROPERTY_CARD_HEIGHT = 245;

export interface PropertyCardProps {
  property: Property;
  carouselDisabled?: boolean;
  hoveredPropertyId?: string | null;
  fullWidth?: boolean;
  width?: number | string;
  showFavoriteButton?: boolean;
}

/** Card with a summary of a buyer listing (images, bed/bath info, etc) */
export const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  carouselDisabled = false,
  fullWidth,
  width = MOBILE_PROPERTY_CARD_WIDTH,
  showFavoriteButton = true,
}) => {
  const { isMobile } = useDevice();
  const imageContainer = useRef<HTMLDivElement | null>(null);

  const [favoriteProperty, { loading: favoriteLoading }] = useFavoriteProperty();
  const [removeFavoriteProperty, { loading: removeFavoriteLoading }] = useRemoveFavoriteProperty();

  const propertyPhotos = property?.photos || [];
  const photos = propertyPhotos
    .map((p) => p?.urls?.size768x512)
    .filter((photoUrl): photoUrl is string => !!photoUrl);

  useMlsPropertyCardEmitter(property.thumbnailLegalLogo);

  const handleFavoriteClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const { addressId } = property;

      if (!property.isFavorite) {
        favoriteProperty({
          variables: { input: { addressId } },
        });
      } else {
        removeFavoriteProperty({
          variables: { input: { addressId } },
        });
      }
    },
    [property, favoriteProperty, removeFavoriteProperty],
  );

  let timeAgoFormatted = null;
  if (property.publishedAt) {
    const duration = Duration.between(
      systemZonedDateTimeFromString(property.publishedAt),
      ZonedDateTime.now(),
    );
    const formattedDuration = formatApproxDuration(duration).replace(
      /\b(minute|minutes)\b/g,
      'min',
    );
    timeAgoFormatted = `${formattedDuration} ago`;
  }

  const [propertyPageUrl, setPropertyPageUrl] = useState('');
  useEffect(() => {
    setPropertyPageUrl(
      `${HOMEPAGE_URL}/properties/aid_${property.addressId}?referrer=${encodeURIComponent(
        window.location.href,
      )}`,
    );
  }, [property?.addressId]);

  return (
    <LegacyExternalLink
      analyticsName="property-page-click"
      href={propertyPageUrl}
      target="_blank"
      aria-label={`${property.street}, ${property.city}, ${property.state}`}
    >
      <Card
        data-propertyid={property.id}
        borderRadius={fullWidth ? 'none' : undefined}
        width={fullWidth || !isMobile ? '100%' : width}
        variant="secondary"
        height="100%"
        padding={0}
        cursor="pointer"
        _hover={{
          boxShadow: 'z2',
        }}
        transition="box-shadow 0.2s ease-in-out"
      >
        <Flex
          flexDirection="column"
          height="100%"
          ref={imageContainer}
          position="relative"
          flexGrow={0}
          role="group"
          aria-label="Property"
        >
          <PropertyCardImageContainer fullWidth={fullWidth}>
            {!carouselDisabled && photos.length >= 2 && (
              <PropertyImageCarousel
                mlsName={property.mlsName}
                photos={photos}
                photoCount={photos.length}
                propertyAddress={`${property.street} ${property.city} ${property.state}`}
              />
            )}

            {(carouselDisabled || photos.length < 2) && (
              <Image
                alt="default property"
                src={
                  photos.length > 0
                    ? photos[0]
                    : googleStreetViewUrl({
                        type: 'address',
                        street: property.street,
                        city: property.city,
                        state: property.state,
                      })
                }
                width="100%"
                height="100%"
                objectFit="cover"
              />
            )}
          </PropertyCardImageContainer>
          <Flex color="neutrals100" justifyContent="space-between" padding={5}>
            <Flex flexDirection="column">
              <Flex alignItems="center">
                <H3>
                  <Text size="s1" mr={3} fontWeight="semibold">
                    {property.currentListPrice
                      ? `$${property.currentListPrice.toLocaleString()}`
                      : ''}
                  </Text>
                </H3>
                {timeAgoFormatted && (
                  <Text size="s00" color="neutrals90">
                    {timeAgoFormatted}
                  </Text>
                )}
              </Flex>
              <Text size="s00" color="neutrals90">
                {property.bedrooms ?? 0} bed {property.bathroomsTotal} bath ·{' '}
                {property.totalLivingSqFt} ft
                <bricks.sup>2</bricks.sup>
              </Text>
              <Text size="s00" color="neutrals90" textOverflow="ellipsis" noOfLines={1}>
                {property.street}
              </Text>
              <Text size="s00" color="neutrals90">
                {property.city}, {property.state}
                {property.thumbnailLegalLogo && '*'}
              </Text>
              {property.thumbnailLegalText && (
                <Text size="s000" color="neutrals90" mt={2}>
                  {property.thumbnailLegalText}
                </Text>
              )}
            </Flex>
          </Flex>
          {property.badges && property.badges.length > 0 && (
            <Box position="absolute" top="4" left="4">
              <ListingBadges
                badges={property.badges as ListingStatus[]}
                openHouses={property.openHouses}
              />
            </Box>
          )}
          {showFavoriteButton && (
            <FavoriteButton
              aria-label={`Favorite property on ${property.street}`}
              isFavorite={property.isFavorite || false}
              onFavoriteClick={handleFavoriteClick}
              isFavoriteResponsePending={favoriteLoading || removeFavoriteLoading}
            />
          )}
        </Flex>
      </Card>
    </LegacyExternalLink>
  );
};
